import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@nexus/ui";
import type { PromptDto } from "@/lib/queries";
import { summarizePrompts } from "@/features/prompts/lib/prompt-kpis";

interface PromptsKpiStripProps {
  prompts: PromptDto[];
  isLoading: boolean;
}

interface KpiTileProps {
  label: string;
  value: number;
  hint?: string;
  /** Draws an amber dot: the count is something an operator should look at. */
  attention?: boolean;
  isLoading: boolean;
}

function KpiTile({ label, value, hint, attention, isLoading }: KpiTileProps) {
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-border bg-card px-3 py-2.5">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="flex items-baseline gap-1.5">
        {isLoading ? (
          <Skeleton className="h-5 w-8" />
        ) : (
          <>
            {attention && (
              <span
                aria-hidden
                className="size-1.5 shrink-0 self-center rounded-full bg-warning"
              />
            )}
            <span className="text-[20px] font-medium leading-none tabular-nums text-foreground">
              {value}
            </span>
            {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
          </>
        )}
      </dd>
    </div>
  );
}

/**
 * Registry health at a glance: coverage, how much of it is tenant-specific, and
 * the two counts worth acting on. Numbers describe the filtered list below so
 * they always agree with the table.
 */
export function PromptsKpiStrip({ prompts, isLoading }: PromptsKpiStripProps) {
  const { t } = useTranslation();
  const kpis = useMemo(() => summarizePrompts(prompts), [prompts]);

  return (
    <dl className="grid grid-cols-2 gap-2 px-4 sm:grid-cols-4">
      <KpiTile
        label={t("prompts.kpi.callSites", "Call sites")}
        value={kpis.callSites}
        isLoading={isLoading}
      />
      <KpiTile
        label={t("prompts.kpi.overrides", "Tenant overrides")}
        value={kpis.overrides}
        hint={t("prompts.kpi.ofTotal", "of {{total}}", { total: kpis.total })}
        isLoading={isLoading}
      />
      <KpiTile
        label={t("prompts.kpi.serviceDefault", "Service default")}
        value={kpis.serviceDefault}
        isLoading={isLoading}
      />
      <KpiTile
        label={t("prompts.status.inactive", "Inactive")}
        value={kpis.inactive}
        attention={kpis.inactive > 0}
        isLoading={isLoading}
      />
    </dl>
  );
}
